<template>
    <div v-if="value || (editable && eligible)" class="request-item-ppe mt-2">
        <b-button v-if="!value" size="sm" variant="outline-primary" @click="enable">
            + Получатели и размеры
        </b-button>
        <component :is="rosterMode ? 'div' : 'details'" v-else :open="editable || defaultOpen">
            <summary v-if="!rosterMode">Получатели и размеры <span class="text-muted">· {{ value.recipients.length }} чел. / {{ item.quantity }} {{ item.unit || item.unit_of_measurement || 'шт.' }}</span></summary>
            <small v-if="value.recipients.some(row => row.employee_id)" class="d-block text-muted my-2">Нажмите на ФИО, чтобы посмотреть контакты.</small>
            <template v-if="editable">
                <div class="request-item-ppe__search">
                    <div v-if="replaceKey" class="small mb-1">Выберите другого сотрудника <b-button size="sm" variant="link" @click="replaceKey = null">Отмена замены</b-button></div>
                    <b-form-input :value="search" placeholder="Начните вводить фамилию сотрудника" aria-label="Поиск получателя" @input="queueSearch" @keydown.enter.prevent="findEmployees(search)" />
                    <div v-if="loading" class="small text-muted py-1">Ищем сотрудников…</div>
                    <div v-if="options.length" class="request-item-ppe__options">
                        <button v-for="option in options" :key="option.employee_id" type="button" @click="selectEmployee(option)">
                            <strong>{{ option.name }}</strong><small>{{ option.can_view ? (formatSizes(option.sizes) || 'В карточке пока нет размеров') : 'Размеры можно указать вручную' }}</small>
                        </button>
                    </div>
                    <b-button v-if="nextOffset !== null && !loading" size="sm" variant="link" @click="findEmployees(search, nextOffset)">Показать ещё</b-button>
                    <small v-else-if="search && !options.length && !loading && !error" class="text-muted">Сотрудники не найдены. Можно ввести ФИО вручную.</small>
                    <b-button size="sm" variant="link" class="pl-0" @click="addManualRecipient">Нет в списке? Ввести ФИО вручную</b-button>
                </div>
                <label v-if="!rosterMode" class="d-block mt-2">Вид СИЗ
                    <b-form-select :value="value.kind" :options="kinds" size="sm" @change="changeKind" />
                </label>
                <div v-for="row in value.recipients" :key="row.key" :ref="`person-${row.key}`" class="request-item-ppe__person">
                    <div class="d-flex align-items-center mb-2">
                        <EmployeeLink v-if="row.employee_id" :employee-id="row.employee_id" :fallback="row.name" compact class="flex-grow-1" />
                        <b-form-input v-else :value="row.name" maxlength="500" size="sm" placeholder="ФИО вручную" @input="setName(row, $event)" />
                        <b-button size="sm" variant="link" @click="replaceKey = row.key; search = ''; options = []">Заменить</b-button>
                        <b-button size="sm" variant="link" class="text-danger" :aria-label="`Удалить получателя ${row.name}`" @click="remove(row)">×</b-button>
                    </div>
                    <div class="request-item-ppe__size-heading">
                        <strong>Размеры сотрудника</strong>
                        <span v-if="cardLoads[row.key]">Загружаем из карточки…</span>
                        <b-button v-if="row.employee_id" size="sm" variant="link" :disabled="cardLoads[row.key]" @click="fillCardSizes(row)">Заполнить пустые из карточки</b-button>
                    </div>
                    <div v-if="cardViews[row.key] && missingCardSizes(row)" class="request-item-ppe__card-sizes">В карточке: {{ formatSizes(currentSizes[row.key]) }}</div>
                    <small v-if="cardViews[row.key] === false" class="d-block text-muted mb-2">Размеры из карточки недоступны. Их можно ввести вручную для этой заявки.</small>
                    <small v-if="cardMessages[row.key]" class="d-block text-muted mb-2" role="status">{{ cardMessages[row.key] }}</small>
                    <small v-if="cardErrors[row.key]" class="d-block text-danger mb-2" role="alert">{{ cardErrors[row.key] }}</small>
                    <div class="request-item-ppe__fields">
                        <label v-for="field in rowFields(row)" :key="field">{{ labels[field] }}
                            <b-form-input :value="row.sizes[field] || ''" :aria-label="`${row.name}: ${labels[field]}`" size="sm" :maxlength="sizeLimit" placeholder="Не указан" @input="setSize(row, field, $event)" />
                        </label>
                    </div>
                    <details class="request-item-ppe__notes" :open="Boolean(row.notes)">
                        <summary>Примечание о сотруднике</summary>
                        <AppTextarea :value="row.notes" size="sm" :maxlength="notesLimit" placeholder="Например: нужна свободная куртка" @input="setNotes(row, $event)" />
                    </details>
                    <slot name="person" :person="row"></slot>
                    <small v-if="row.employee_id && rights[row.key] === false" class="d-block text-warning mt-1">Размеры сохранятся только в заявке: нет права редактирования СИЗ.</small>
                    <small v-for="result in rowResults(row)" :key="result.field" class="d-block mt-1" :class="result.status === 'updated' ? 'text-success' : 'text-muted'">{{ labels[result.field] }}: {{ syncLabels[result.status] }}</small>
                </div>
                <small v-if="value.recipients.length" class="d-block text-muted">Проверьте размеры. Исправления обновят карточку при отправке, если у вас есть право её редактировать.</small>
                <small v-if="!rosterMode" class="d-block text-muted">Количество вводится отдельно. Для расходников и запаса получателей можно не указывать.</small>
                <b-button v-if="!rosterMode" size="sm" variant="link" class="pl-0 text-muted" @click="$emit('input', null)">Убрать блок</b-button>
            </template>
            <template v-else>
                <ul class="request-item-ppe__snapshot">
                    <li v-for="row in snapshotRows" :key="row.key" class="request-item-ppe__snapshot-person" :class="{'request-item-ppe__snapshot-person--stock': row.stock}">
                        <EmployeeLink v-if="!row.stock && row.employee_id" :employee-id="row.employee_id" :fallback="row.name" compact class="request-item-ppe__snapshot-name" />
                        <strong v-else class="request-item-ppe__snapshot-name">{{ row.name }}</strong>
                        <small v-if="!row.stock && !row.employee_id" class="d-block text-muted">Не связан с карточкой</small>
                        <div class="request-item-ppe__snapshot-values">
                            <span class="request-item-ppe__snapshot-quantity">Количество: <b>{{ row.quantity }} {{ item.unit || item.unit_of_measurement || 'шт.' }}</b></span>
                            <span v-for="size in row.sizes" :key="size.field">{{ labels[size.field] }}: <b>{{ size.value }}</b></span>
                        </div>
                        <div v-if="row.notes" class="request-item-ppe__snapshot-notes">{{ row.notes }}</div>
                    </li>
                </ul>
                <small v-for="(result, index) in value.sync_results || []" :key="index" class="d-block text-muted">{{ result.name }} · {{ labels[result.field] }}: {{ syncLabels[result.status] }}</small>
            </template>
        </component>
        <small v-for="(result, index) in warnings" :key="`warning-${index}`" class="d-block text-warning">{{ result.name }} · {{ labels[result.field] }}: {{ syncLabels[result.status] }}</small>
        <small v-if="error" class="d-block text-danger">{{ error }}</small>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import api from '@/utils/api';
import {KINDS, SIZE_LABELS, SYNC_LABELS, ppeFields, ppeEligible} from '@/utils/requestPpe';
import {PPE_SIZE_LIMIT, PPE_NOTES_LIMIT} from '@/utils/ppeSizes';

const configs = new WeakMap();
let recipientSequence = 0;
const clone = value => JSON.parse(JSON.stringify(value));
export default {
    components: {EmployeeLink},
    name: 'RequestItemPpe',
    props: {
        value: {type: Object, default: null},
        item: {type: Object, required: true},
        editable: {type: Boolean, default: false},
        defaultOpen: {type: Boolean, default: false},
        rosterMode: {type: Boolean, default: false},
        requestId: {type: [String, Number], default: null},
        requestTypeId: {type: [String, Number], default: null}
    },
    data() {
        return {config: null, search: '', options: [], loading: false, error: '', replaceKey: null, nextOffset: null,
            rights: {}, currentSizes: {}, cardViews: {}, cardLoads: {}, cardErrors: {}, cardMessages: {}, cardVersion: 0, searchVersion: 0, kinds: KINDS.map(kind => ({value: kind.value, text: kind.text})),
            labels: SIZE_LABELS, syncLabels: SYNC_LABELS, sizeLimit: PPE_SIZE_LIMIT, notesLimit: PPE_NOTES_LIMIT};
    },
    computed: {
        fields() { return ppeFields(this.value && this.value.kind); },
        snapshotRows() {
            const data = this.value || {};
            const rows = (data.recipients || []).map((row, index) => ({...row, key: `person-${row.key || index}`, stock: false}));
            (data.stock || []).forEach((row, index) => rows.push({...row, key: `stock-${row.key || index}`, name: 'В запас', stock: true}));
            return rows.map(row => ({...row, quantity: row.quantity || 1,
                sizes: this.fields.filter(field => row.sizes && row.sizes[field]).map(field => ({field, value: row.sizes[field]}))}));
        },
        warnings() { return (this.value && this.value.sync_results || []).filter(result => !['updated', 'unchanged'].includes(result.status)); },
        eligible() { return ppeEligible(this.config, this.item); },
        contextKey() { return `${this.requestId || ''}:${this.requestTypeId || ''}:${this.editable}`; }
    },
    watch: {contextKey: {immediate: true, handler() { this.loadConfig(); }}},
    beforeDestroy() { this.searchVersion += 1; this.cardVersion += 1; clearTimeout(this.searchTimer); },
    methods: {
        rowFields() { return this.rosterMode ? Object.keys(SIZE_LABELS) : this.fields; },
        formatSizes(sizes) { return Object.keys(SIZE_LABELS).filter(field => sizes && sizes[field]).map(field => `${this.labels[field]} ${sizes[field]}`).join(' · '); },
        missingCardSizes(row) { return this.rowFields(row).some(field => !row.sizes[field] && this.currentSizes[row.key] && this.currentSizes[row.key][field]); },
        focusPerson(person) {
            const elements = this.$refs[`person-${person.key}`];
            const element = Array.isArray(elements) ? elements[0] : elements;
            if (element) {
                const input = element.querySelector('input');
                (input || element).scrollIntoView({behavior: 'smooth', block: 'center'});
                if (input) input.focus({preventScroll: true});
            }
        },
        async addManualRecipient() {
            const row = this.selectEmployee({name: this.search, employee_id: null});
            await this.$nextTick();
            this.focusPerson(row);
        },
        params() { return {request_id: this.requestId || undefined, request_type_id: this.requestTypeId || undefined}; },
        async loadConfig() {
            if (!this.editable) return;
            this.cardVersion += 1;
            this.rights = {}; this.cardViews = {}; this.currentSizes = {}; this.cardLoads = {}; this.cardErrors = {}; this.cardMessages = {};
            const key = this.contextKey;
            if (!configs.has(this.$parent)) configs.set(this.$parent, new Map());
            const cache = configs.get(this.$parent);
            if (!cache.has(key)) cache.set(key, api.get('/api/requests/items/ppe-options', {params: this.params(), ignoreModal: true}).catch(error => { cache.delete(key); throw error; }));
            try {
                const response = await cache.get(key);
                if (key !== this.contextKey) return;
                this.config = response.data;
                for (const row of this.value && this.value.recipients || []) this.loadRights(row);
            } catch (error) { this.error = 'Не удалось загрузить настройки получателей СИЗ.'; }
        },
        async loadRights(row) {
            if (!row.employee_id || (this.config && !this.config.enabled && !this.requestId)) return;
            const version = this.cardVersion;
            const context = this.contextKey;
            this.$set(this.cardLoads, row.key, true);
            this.$set(this.cardErrors, row.key, '');
            try {
                const response = await api.get('/api/requests/items/ppe-options', {params: {...this.params(), mode: 'employees', employee_id: row.employee_id}, ignoreModal: true});
                if (version !== this.cardVersion || context !== this.contextKey || !(this.value && this.value.recipients.some(current => current.key === row.key && current.employee_id === row.employee_id))) return;
                const employee = response.data.items[0];
                this.$set(this.rights, row.key, Boolean(employee && employee.can_manage));
                this.$set(this.cardViews, row.key, Boolean(employee && employee.can_view));
                this.$set(this.currentSizes, row.key, employee && employee.can_view ? employee.sizes : {});
                // The saved snapshot remains untouched; only an explicit edit can change it.
                return employee;
            } catch (error) {
                if (version === this.cardVersion && context === this.contextKey) this.$set(this.cardErrors, row.key, 'Не удалось загрузить размеры. Нажмите «Заполнить пустые из карточки», чтобы повторить.');
            } finally { if (version === this.cardVersion && context === this.contextKey) this.$set(this.cardLoads, row.key, false); }
        },
        async fillCardSizes(row) {
            this.$set(this.cardMessages, row.key, '');
            const employee = await this.loadRights(row);
            if (!employee || !employee.can_view) return;
            let filled = 0;
            this.change(data => {
                const current = data.recipients.find(value => value.key === row.key && value.employee_id === row.employee_id);
                if (!current) return;
                for (const field of this.rowFields(current)) {
                    if (current.sizes[field] || (current.dirty_fields || []).includes(field) || !employee.sizes[field]) continue;
                    current.sizes[field] = employee.sizes[field]; current.baseline[field] = employee.sizes[field]; filled++;
                }
            });
            this.$set(this.cardMessages, row.key, filled ? 'Пустые размеры заполнены из карточки.' : 'Заполненные и исправленные поля оставлены как есть. Других размеров в карточке нет.');
        },
        enable() { this.$emit('input', {version: 1, kind: 'kit', recipients: []}); },
        change(fn) { const data = clone(this.value); fn(data); this.$emit('input', data); },
        rowResults(row) { return (this.value.sync_results || []).filter(result => result.key === row.key); },
        setName(row, name) { this.change(data => { data.recipients.find(item => item.key === row.key).name = name; }); },
        setNotes(row, notes) { this.change(data => { data.recipients.find(item => item.key === row.key).notes = notes; }); },
        setSize(row, field, value) {
            this.change(data => {
                const current = data.recipients.find(item => item.key === row.key);
                if (!(current.dirty_fields || []).includes(field) && this.currentSizes[row.key]) {
                    current.baseline[field] = this.currentSizes[row.key][field] || null;
                }
                current.sizes[field] = value;
                current.dirty_fields = Array.from(new Set([...(current.dirty_fields || []), field]));
                data.sync_results = (data.sync_results || []).filter(result => result.key !== row.key || result.field !== field);
            });
        },
        remove(row) { this.change(data => { data.recipients = data.recipients.filter(item => item.key !== row.key); }); },
        changeKind(kind) {
            this.change(data => {
                data.kind = kind;
                data.recipients.forEach(row => {
                    for (const field of Object.keys(row.sizes)) if (!ppeFields(kind).includes(field)) delete row.sizes[field];
                    row.dirty_fields = (row.dirty_fields || []).filter(field => ppeFields(kind).includes(field));
                });
            });
        },
        queueSearch(search) {
            clearTimeout(this.searchTimer);
            this.searchVersion += 1;
            this.search = search;
            this.options = [];
            this.nextOffset = null;
            this.loading = Boolean(search.trim());
            if (this.loading) this.searchTimer = setTimeout(() => this.findEmployees(search), 250);
        },
        async findEmployees(search, offset = 0) {
            clearTimeout(this.searchTimer);
            this.search = search;
            const version = ++this.searchVersion;
            if (!offset) this.options = [];
            this.nextOffset = null;
            if (!search.trim()) { this.loading = false; return; }
            this.loading = true;
            this.error = '';
            try {
                const response = await api.get('/api/requests/items/ppe-options', {params: {...this.params(), mode: 'employees', search, offset}, ignoreModal: true});
                if (version === this.searchVersion) {
                    this.options = offset ? this.options.concat(response.data.items) : response.data.items;
                    this.nextOffset = response.data.next_offset == null ? null : response.data.next_offset;
                }
            } catch (error) { if (version === this.searchVersion) this.error = 'Не удалось найти сотрудников. Можно ввести ФИО вручную.'; }
            finally { if (version === this.searchVersion) this.loading = false; }
        },
        selectEmployee(employee) {
            if (employee.employee_id && this.value.recipients.some(row => row.key !== this.replaceKey && row.employee_id === employee.employee_id)) {
                this.error = 'Этот сотрудник уже добавлен.'; return;
            }
            clearTimeout(this.searchTimer);
            const row = {key: `ppe-${Date.now()}-${++recipientSequence}`, employee_id: employee.employee_id || null, quantity: 1,
                name: employee.name || '', sizes: {}, baseline: {}, notes: '', dirty_fields: []};
            if (this.replaceKey) row.key = this.replaceKey;
            for (const field of this.fields) {
                row.baseline[field] = employee.sizes && employee.sizes[field] || null;
                row.sizes[field] = row.baseline[field] || '';
            }
            if (employee.sizes) this.$set(this.currentSizes, row.key, employee.sizes);
            this.$set(this.cardViews, row.key, employee.can_view === true);
            this.$set(this.cardErrors, row.key, ''); this.$set(this.cardMessages, row.key, '');
            this.$set(this.rights, row.key, employee.can_manage === true);
            this.change(data => {
                const index = data.recipients.findIndex(item => item.key === this.replaceKey);
                if (index >= 0) data.recipients.splice(index, 1, row); else data.recipients.push(row);
            });
            this.replaceKey = null;
            this.search = '';
            this.options = [];
            this.nextOffset = null;
            this.searchVersion += 1;
            this.loading = false;
            return row;
        }
    }
};
</script>

<style scoped>
.request-item-ppe { text-align: left; font-size: .95rem; min-width: 220px; max-width: 960px; }
.request-item-ppe summary { color: #27597b; cursor: pointer; padding: 5px 0; font-weight: 600; }
.request-item-ppe__search { margin: 14px 0; max-width: 650px; }
.request-item-ppe__person { border: 1px solid #d8e2ec; border-left: 3px solid #6196c6; border-radius: 8px; background: #fff; padding: 16px; margin: 12px 0; }
.request-item-ppe__size-heading { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin: 4px 0 8px; font-size: .85rem; color: #526a80; }
.request-item-ppe__size-heading .btn { padding-left: 0; }
.request-item-ppe__card-sizes { background: #eaf4ff; color: #245781; border-radius: 5px; padding: 8px 10px; margin-bottom: 10px; font-size: .9rem; }
.request-item-ppe__fields { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; }
.request-item-ppe__fields label { font-weight: 400; color: #536a7d; font-size: .85rem; margin: 0; }
.request-item-ppe__fields input { height: 38px; font-size: 1rem; font-weight: 600; color: #253d52; margin-top: 4px; }
.request-item-ppe__fields input::placeholder { font-size: .8rem; font-weight: 400; color: #8191a0; }
.request-item-ppe__notes { margin-top: 8px; font-size: .85rem; }
.request-item-ppe__notes summary { font-weight: 400; }
.request-item-ppe__snapshot { list-style: none; padding: 0; margin: 12px 0 0; }
.request-item-ppe__snapshot-person { border: 1px solid #b9ccdd; border-left: 4px solid #648daf; border-radius: 7px; padding: 12px 14px; margin: 0 0 14px; background: #f5f8fc; overflow-wrap: anywhere; }
.request-item-ppe__snapshot-person:last-child { margin-bottom: 0; }
.request-item-ppe__snapshot-person--stock { border-left-color: #8c9caa; background: #f7f8fa; }
.request-item-ppe__snapshot-name { display: block; padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px solid #d3dfeb; font-size: 1rem; line-height: 1.4; color: #263f55; }
.request-item-ppe__snapshot-values { display: flex; flex-wrap: wrap; gap: 7px 18px; font-size: .9rem; line-height: 1.5; color: #435b70; }
.request-item-ppe__snapshot-quantity { flex-basis: 100%; }
.request-item-ppe__snapshot-notes { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #d3dfeb; white-space: pre-line; font-size: .9rem; color: #52677a; }
.request-item-ppe__options { max-height: 210px; overflow-y: auto; border: 1px solid #dce5ed; }
.request-item-ppe__options button { display: block; width: 100%; text-align: left; padding: 8px; border: 0; background: white; }
.request-item-ppe__options button:hover, .request-item-ppe__options button:focus { background: #e8f2fc; }
.request-item-ppe__options small { display: block; color: #536a7d; margin-top: 3px; }
@media (max-width: 767px) { .request-item-ppe__fields { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 400px) { .request-item-ppe__fields { grid-template-columns: repeat(2, minmax(0, 1fr)); } .request-item-ppe__person { padding: 12px; } }
</style>
